/**
 * Sanitizes Redux state for Next.js serialization
 * Converts undefined values to null and removes non-serializable data
 * Optimized for performance - runs on every server-side page request
 */
export const sanitizeForSerialization = obj => {
  // Fast path: primitives and null (most common in deeply nested state)
  if (obj === null || obj === undefined) return null;

  const type = typeof obj;

  // Primitives pass through unchanged (except non-finite numbers)
  if (type === 'string' || type === 'boolean') {
    return obj;
  }

  // Numbers must be finite to be JSON-serializable
  // NaN, Infinity, -Infinity are not valid JSON (JSON.stringify converts to null)
  if (type === 'number') {
    return Number.isFinite(obj) ? obj : null;
  }

  // Non-objects (functions, symbols, bigint, etc.) become null
  if (type !== 'object') return null;

  if (Array.isArray(obj)) {
    const { length } = obj;
    const result = new Array(length);
    for (let i = 0; i < length; i++) {
      result[i] = sanitizeForSerialization(obj[i]);
    }
    return result;
  }

  // Handle objects with toJSON method (like Date)
  // JSON.stringify calls toJSON() if it exists, so we should too
  if (typeof obj.toJSON === 'function') {
    const jsonValue = obj.toJSON();
    // Recursively sanitize the toJSON result
    // (e.g., Date.toJSON() returns a string, which passes through)
    return sanitizeForSerialization(jsonValue);
  }

  const keys = Object.keys(obj);
  const sanitized = {};
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = obj[key];
    // Skip undefined and function values entirely (not valid JSON)
    // JSON.stringify omits these properties completely
    if (value !== undefined && typeof value !== 'function') {
      sanitized[key] = sanitizeForSerialization(value);
    }
  }
  return sanitized;
};

/**
 * Deserializes state from Next.js
 *
 * This function receives the serialized Redux state from Next.js and prepares it
 * for client-side hydration. Currently implements a pass-through pattern, but serves
 * as an extension point for future deserialization logic such as:
 * - Converting ISO date strings back to Date objects
 * - Restoring class instances
 * - Rebuilding complex data structures
 * - Applying client-side transformations
 *
 * @param {Object} state - The serialized Redux state from Next.js getServerSideProps
 * @returns {Object} The deserialized state ready for Redux store hydration
 *
 * @example
 * const clientState = deserializeState(serverState);
 * store.dispatch({ type: 'HYDRATE', payload: clientState });
 */
export const deserializeState = state => {
  return state;
};
